import {QueryCacheKeys} from '../../api/cache';
import {getTransactions} from '../../api/transactions';
import {useLocalStorageState} from '../../hooks';
import {downloadTransactionCsv} from '../../util/csv';
import {useIsMobile} from '../../util/mobile';
import CategoryPicker from '../CategoryPicker';
import Layout from '../Layout';
import ProjectPickerModal from '../forms/ProjectPickerModal';
import ReportingPeriodPicker from '../forms/ReportingPeriodPicker';
import TagPickerModal from '../tags/TagPickerModal';
import TransactionsTable from './TransactionsTable';
import {Box, Button, Icon, Input, LoaderButton} from '@sproutsocial/racine';
import {useQuery} from '@tanstack/react-query';
import {DateTime} from 'luxon';
import {SyntheticEvent, useCallback, useMemo, useState} from 'react';
import {debounce} from 'shared/src/function';
import {orderBy} from 'shared/src/list';
import type {TransactionFilters} from 'shared/src/models';

const TransactionsList = () => {
	const isMobile = useIsMobile();

	const [startDate, setStartDate] = useLocalStorageState<DateTime>({
		localStorageKey: 'transaction-list.start-date',
		defaultValue: DateTime.now().startOf('month'),
		serialize: (datetime) => datetime.toISO(),
		deserialize: (json) => DateTime.fromISO(json),
	});

	const [endDate, setEndDate] = useLocalStorageState<DateTime>({
		localStorageKey: 'transaction-list.end-date',
		defaultValue: DateTime.now(),
		serialize: (datetime) => datetime.toISO(),
		deserialize: (json) => DateTime.fromISO(json),
	});

	const onReportingPeriodChange = useCallback(
		(startDate: DateTime, endDate: DateTime) => {
			setStartDate(startDate);
			setEndDate(endDate);
		},
		[setStartDate, setEndDate],
	);

	const [selectedCategoryIds, setSelectedCategoryIds] = useLocalStorageState<Array<string>>({
		localStorageKey: 'transaction-list.categories',
		defaultValue: [],
	});

	const [selectedProjectIds, setSelectedProjectIds] = useLocalStorageState<Set<string>>({
		localStorageKey: 'transaction-list.projects',
		defaultValue: new Set(),
		serialize: (set) => JSON.stringify(Array.from(set)),
		deserialize: (json) => new Set<string>(JSON.parse(json) as string[]),
	});

	const [selectedTagIds, setSelectedTagIds] = useLocalStorageState<Set<string>>({
		localStorageKey: 'transaction-list.tags',
		defaultValue: new Set(),
		serialize: (set) => JSON.stringify(Array.from(set)),
		deserialize: (json) => new Set<string>(JSON.parse(json) as string[]),
	});

	const [excludedTagIds, setExcludedTagIds] = useLocalStorageState<Set<string>>({
		localStorageKey: 'transaction-list.excluded-tags',
		defaultValue: new Set(),
		serialize: (set) => JSON.stringify(Array.from(set)),
		deserialize: (json) => new Set<string>(JSON.parse(json) as string[]),
	});

	const [filter, setFilter] = useState('');
	const [normalizedFilter, setNormalizedFilter] = useState(filter);

	const queryFilters: TransactionFilters = {
		startDate,
		endDate,
		categoryIds: selectedCategoryIds,
		tagIds: Array.from(selectedTagIds),
		excludedTagIds: Array.from(excludedTagIds),
		projectIds: Array.from(selectedProjectIds),
	};

	const {data: rawTransactions, refetch: refetchTransactions} = useQuery({
		queryKey: QueryCacheKeys.transactions(queryFilters),
		queryFn: () => getTransactions(queryFilters),
	});

	const transactions = useMemo(() => {
		if (!rawTransactions) {
			return;
		}

		const normalizedTransactions = rawTransactions.filter((transaction) => {
			if (!normalizedFilter) {
				return true;
			}

			return (
				transaction.payee?.toLocaleLowerCase().includes(normalizedFilter) ||
				transaction.memo.toLocaleLowerCase().includes(normalizedFilter)
			);
		});

		return orderBy(normalizedTransactions, 'date', 'desc');
	}, [rawTransactions, normalizedFilter]);

	const tableKey = useMemo(() => {
		return transactions?.map((transaction) => transaction.id).join();
	}, [transactions]);

	const [isCategoryPickerOpen, setIsCategoryPickerOpen] = useState(false);
	const openCategoryPicker = useCallback(
		() => setIsCategoryPickerOpen(true),
		[setIsCategoryPickerOpen],
	);
	const onCategoryPickerClose = useCallback(
		(newSelectedCategoryIds: Array<string>) => {
			setSelectedCategoryIds(newSelectedCategoryIds);
			setIsCategoryPickerOpen(false);
		},
		[setSelectedCategoryIds],
	);

	const [isProjectPickerOpen, setIsProjectPickerOpen] = useState(false);
	const openProjectPicker = useCallback(
		() => setIsProjectPickerOpen(true),
		[setIsProjectPickerOpen],
	);
	const onProjectPickerSave = useCallback(
		(newSelectedProjectIds: Set<string>) => {
			setSelectedProjectIds(newSelectedProjectIds);
			setIsProjectPickerOpen(false);
		},
		[setSelectedProjectIds],
	);
	const onProjectPickerCancel = useCallback(() => {
		setIsProjectPickerOpen(false);
	}, []);

	const [isTagPickerOpen, setIsTagPickerOpen] = useState(false);
	const openTagPicker = useCallback(() => setIsTagPickerOpen(true), [setIsTagPickerOpen]);
	const onTagPickerSave = useCallback(
		(newSelectedTagIds: Set<string>, newExcludedTagIds: Set<string>) => {
			setSelectedTagIds(newSelectedTagIds);
			setExcludedTagIds(newExcludedTagIds);
			setIsTagPickerOpen(false);
		},
		[setSelectedTagIds, setExcludedTagIds],
	);
	const onTagPickerCancel = useCallback(() => {
		setIsTagPickerOpen(false);
	}, []);

	const updateNormalizedFilter = useMemo<(filter: string) => void>(() => {
		return debounce((filter: string) => {
			setNormalizedFilter(filter.trim().toLocaleLowerCase());
		}, 500);
	}, []);

	const onFilterChange = useCallback(
		(event: SyntheticEvent<HTMLInputElement>) => {
			setFilter(event.currentTarget.value);
			updateNormalizedFilter(event.currentTarget.value);
		},
		[setFilter, updateNormalizedFilter],
	);

	const [csvGenerating, setCsvGenerating] = useState<boolean>(false);
	const onDownloadCsv = useCallback(async () => {
		if (transactions) {
			setCsvGenerating(true);

			await downloadTransactionCsv(transactions, 'transactions.csv');

			setCsvGenerating(false);
		}
	}, [transactions, setCsvGenerating]);

	return (
		<Layout>
			<Layout.Header title='Transactions'></Layout.Header>
			<Layout.Body>
				<Box
					display='flex'
					flexWrap='wrap'
					rowGap='space.300'
					p={isMobile ? 'space.300' : 0}
					mb={isMobile ? 0 : 'space.400'}
				>
					<ReportingPeriodPicker
						startDate={startDate}
						endDate={endDate}
						onChange={onReportingPeriodChange}
						mr='space.300'
					/>
					<Button
						appearance='secondary'
						onClick={openCategoryPicker}
						active={selectedCategoryIds.length > 0}
						mr='space.300'
					>
						<Box display='flex' alignItems='center'>
							<Icon name='folder' size='mini' mr='space.300' /> Categories
						</Box>
					</Button>
					<Button
						appearance='secondary'
						onClick={openProjectPicker}
						active={selectedProjectIds.size > 0}
						mr='space.300'
					>
						<Box display='flex' alignItems='center'>
							<Icon name='screwdriver-hammer-solid' size='mini' mr='space.300' /> Projects
						</Box>
					</Button>
					<Button
						appearance='secondary'
						onClick={openTagPicker}
						active={selectedTagIds.size > 0}
						mr='space.300'
					>
						<Box display='flex' alignItems='center'>
							<Icon name='tag' size='mini' mr='space.300' /> Tags
						</Box>
					</Button>
					<Box minWidth={100} maxWidth={300}>
						<Input
							id='transaction-filter'
							name='transaction-filter'
							placeholder='Filter transactions...'
							value={filter}
							onChange={onFilterChange}
							mr='space.300'
						/>
					</Box>
					<LoaderButton
						appearance='secondary'
						isLoading={csvGenerating}
						onClick={onDownloadCsv}
						mr='space.300'
					>
						<Icon name='arrow-up-from-bracket-solid' mr='space.300' /> Export CSV
					</LoaderButton>
				</Box>
				{transactions && (
					<TransactionsTable
						id='main-transactions'
						key={tableKey}
						transactions={transactions}
						onChange={refetchTransactions}
					/>
				)}

				{isCategoryPickerOpen && (
					<CategoryPicker
						selectedCategoryIds={selectedCategoryIds}
						onClose={onCategoryPickerClose}
					/>
				)}

				{isProjectPickerOpen && (
					<ProjectPickerModal
						title='Filter Projects'
						selectedProjectIds={selectedProjectIds}
						onSave={onProjectPickerSave}
						onCancel={onProjectPickerCancel}
					/>
				)}

				{isTagPickerOpen && (
					<TagPickerModal
						selectedTagIds={selectedTagIds}
						allowExclusions
						excludedTagIds={excludedTagIds}
						onSave={onTagPickerSave}
						onCancel={onTagPickerCancel}
					/>
				)}
			</Layout.Body>
		</Layout>
	);
};

export default TransactionsList;
